<template>
  <div>
    <info-section>
      <template v-slot:header>
        <slot name="label">{{infoTitle}}</slot>
      </template>
      <div class="info-section__label">
        <slot name="header">
          <template v-if="dataRFQs.rfqid">
            <span class="title">{{L10n('rfqid')}}: </span><span>{{dataRFQs.rfqid}}</span>
          </template>
        </slot>
      </div>
      <template>
        <div v-for="(info) in showedList" :key="info.fieldName">
          <span class="title">{{info.title}}: </span>
          <span>{{info.value}}</span>
        </div>
        <el-collapse v-model="activeItems" v-if="Boolean(customerCreditTerms) || Boolean(customerRemarks.length)">
          <el-collapse-item v-if="Boolean(customerCreditTerms)" name="credit-terms">
            <template slot="title">
              <span class="title">{{L10n('customer-credit-terms')}}</span>
            </template>
            <el-descriptions :column="2" size="mini" border>
              <el-descriptions-item :label="L10n('credit-days')" :span="2">
                {{customerCreditTerms.creditDays}}
              </el-descriptions-item>
              <el-descriptions-item :label="L10n('supplier-credit')" :span="2">
                {{customerCreditTerms.supplierCredit}}
              </el-descriptions-item>
              <el-descriptions-item :label="L10n('credit-line')" :span="2">
                {{customerCreditTerms.creditLine}}
              </el-descriptions-item>
              <el-descriptions-item :label="L10n('coface-credit')" :span="2">
                {{customerCreditTerms.cofaceCredit}}
              </el-descriptions-item>
              <el-descriptions-item :label="L10n('currency')" :span="2">
                {{dataSourceCurrency.map[customerCreditTerms.currencyID] && dataSourceCurrency.map[customerCreditTerms.currencyID].title}}
              </el-descriptions-item>
            </el-descriptions>
          </el-collapse-item>
          <el-collapse-item v-if="Boolean(customerRemarks.length)" name="customer-remarks">
            <template slot="title">
              <span class="title">{{L10n('customer-remarks')}}</span>
            </template>
            <ul>
              <li v-for="(remark, i) in customerRemarks" :key="i">
                <p :title="remark.remark" class="company-remark">{{remark.remark}}</p>
              </li>
            </ul>
          </el-collapse-item>
        </el-collapse>
      </template>
    </info-section>
  </div>

</template>

<script>
import { toDate } from '@/utils/filters'
export default {
  name: 'InfoDataRFQs',
  components: {
    'info-section': () => import(/* webpackChunkName: 'InfoSection' */ '@/components/InfoSection')
  },
  props: {
    title: {
      type: String
    },
    data: {
      type: Object,
      default: () => ({})
    },
    model: {
      type: Function,
      default: () => ({
        rfqid: null,
        customerCompanyID: null,
        customerCompanyDetailID: null,
        customerContactID: null,
        rfqTypeID: null,
        baxEmployeeID: null,
        priorityID: null
      })
    }
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler (data) {
        this.dataRFQs = this.copyObjectProps(this.model(), data)
      }
    }
  },
  data () {
    return {
      dataRFQs: {},
      activeItems: ['credit-terms']
    }
  },
  computed: {
    config () {
      const companySource = this.dataSourceCompany.map[this.dataRFQs.customerCompanyID]
      const companyValue = companySource && companySource.name

      const rfqTypeSource = this.dataSourceRfqType.map[this.dataRFQs.rfqTypeID]
      const rfqTypeValue = rfqTypeSource && rfqTypeSource.title

      const employeeSource = this.dataSourceEmployee.map[this.dataRFQs.baxEmployeeID]
      const employeeValue = employeeSource && employeeSource.name

      const prioritySource = this.dataSourcePriority.map[this.dataRFQs.priorityID]
      const priorityValue = prioritySource && prioritySource.title

      const companyDetailsSource = this.dataSourceCompanyDetails.map[this.dataRFQs.customerCompanyDetailID]
      const companyDetailsValue = companyDetailsSource && companyDetailsSource.country

      const companyContactsSource = this.dataSourceContacts.map[this.dataRFQs.customerContactID]
      const companyContactsValue = companyContactsSource && companyContactsSource.name

      const scoreSource = this.dataSourceScore.map[this.dataRFQs.orderRatingScoreID]
      const scoreValue = scoreSource && scoreSource.scoreText

      const paymentTermSource = this.dataSourcePaymentTerm.map[this.dataRFQs.paymentTermID]
      const paymentTermValue = paymentTermSource && paymentTermSource.title

      const shipSource = this.dataSourceShip.map[this.dataRFQs.shipToCompanyID]
      const shipValue = shipSource && shipSource.name

      const shipDetailsSource = this.dataSourceShipDetails.map[this.dataRFQs.shipToCompanyDetailID]
      const shipDetailValue = shipDetailsSource && shipDetailsSource.country

      const shipContactsSource = this.dataSourceShipContacts.map[this.dataRFQs.shipToContactID]
      const shipContactsValue = shipContactsSource && shipContactsSource.name

      const forwarderSource = this.dataSourceForwarder.map[this.dataRFQs.customerForwarderID]
      const forwarderValue = forwarderSource && forwarderSource.name

      return [
        {
          title: this.L10n('customer-name'),
          value: companyValue
        },
        {
          title: this.L10n('contact-detail'),
          value: companyDetailsValue
        },
        {
          title: this.L10n('contact'),
          value: companyContactsValue
        },
        {
          title: this.L10n('rfq-type'),
          value: rfqTypeValue
        },
        {
          title: this.L10n('employee'),
          value: employeeValue
        },
        {
          title: this.L10n('priority'),
          value: priorityValue
        },
        {
          title: this.L10n('po'),
          value: this.dataRFQs.po
        },
        {
          title: this.L10n('po-date'),
          value: toDate(this.dataRFQs.poDate)
        },
        {
          title: this.L10n('requested-date'),
          value: toDate(this.dataRFQs.requestedDate)
        },
        {
          title: this.L10n('order-rating-score'),
          value: scoreValue
        },
        {
          title: this.L10n('payment-term'),
          value: paymentTermValue
        },
        {
          title: this.L10n('ship'),
          value: shipValue
        },
        {
          title: this.L10n('ship-detail'),
          value: shipDetailValue
        },
        {
          title: this.L10n('ship-contact'),
          value: shipContactsValue
        },
        {
          title: this.L10n('customer-forwarder'),
          value: forwarderValue
        }
      ]
    },
    showedList () {
      return this.config.filter(({ value }) => !!value)
    },
    infoTitle () {
      if (!this.title) {
        return this.L10n('rfq')
      } else {
        return this.title
      }
    },
    dataSourceCompany () {
      if (!this.dataRFQs.customerCompanyID) {
        return {
          records: [],
          map: {}
        }
      }
      return this.API(`Companies/GetAllDynamic?select=id,name,creditTerms,companyRemarks.Select(k => new { k.remark, k.id,  new { k.companyRemarkSubject.id } as companyRemarkSubject }) as companyRemarks&filter=id == ${this.dataRFQs.customerCompanyID}`)
    },
    customerRemarks () {
      const companySource = this.dataSourceCompany.map[this.dataRFQs.customerCompanyID]
      const companyRemarks = companySource && companySource.companyRemarks

      return (companyRemarks || []).filter((remark) => remark.companyRemarkSubject.id === 10)
    },
    customerCreditTerms () {
      const companySource = this.dataSourceCompany.map[this.dataRFQs.customerCompanyID]
      const creditTerms = companySource && companySource.creditTerms && companySource.creditTerms[0]

      return creditTerms || null
    },
    dataSourceCompanyDetails () {
      if (!this.dataRFQs.customerCompanyID) {
        return {
          records: [],
          map: {}
        }
      }
      return this.API(`CompanyDetails/GetAllDynamic?select=id,country&filter=companyID == ${this.dataRFQs.customerCompanyID}`)
    },
    dataSourceContacts () {
      if (!this.dataRFQs.customerCompanyID) {
        return {
          records: [],
          map: {}
        }
      }
      return this.API(`Contacts/GetAllDynamic?select=id,name&filter=companyID == ${this.dataRFQs.customerCompanyID}`)
    },
    dataSourceShip () {
      if (!this.dataRFQs.shipToCompanyID) {
        return {
          records: [],
          map: {}
        }
      }
      return this.API(`Companies/GetAllDynamic?select=id,name&filter=id == ${this.dataRFQs.shipToCompanyID}`)
    },
    dataSourceShipDetails () {
      if (!this.dataRFQs.shipToCompanyDetailID) {
        return {
          records: [],
          map: {}
        }
      }
      return this.API(`CompanyDetails/GetAllDynamic?select=id,country&filter=companyID == ${this.dataRFQs.shipToCompanyID}`)
    },
    dataSourceShipContacts () {
      if (!this.dataRFQs.shipToContactID) {
        return {
          records: [],
          map: {}
        }
      }
      return this.API(`Contacts/GetAllDynamic?select=id,name&filter=companyID == ${this.dataRFQs.shipToCompanyID}`)
    },
    dataSourceForwarder () {
      if (!this.dataRFQs.customerForwarderID) {
        return {
          records: [],
          map: {}
        }
      }
      return this.API(`Forwarders/GetAllDynamic?select=id,shipper.shipperName as name&filter=id == ${this.dataRFQs.customerForwarderID}`)
    },
    dataSourceRfqType () {
      return this.API('RFQTypes/GetAllDynamic?select=id,title')
    },
    dataSourceEmployee () {
      return this.API('Employees/GetAllDynamic?select=id,name&filter=isActive==true')
    },
    dataSourcePriority () {
      return this.API('Priorities/GetAllDynamic?select=id,title')
    },
    dataSourceScore () {
      return this.API('Scores/GetAllDynamic?select=id,scoreText,scoreIndex&orderBy=scoreIndex')
    },
    dataSourcePaymentTerm () {
      return this.API('PaymentTerms/GetAllDynamic?select=id,title')
    },
    dataSourceCurrency () {
      return this.API('Currencies/GetAllDynamic?select=id,title')
    }
  }
}
</script>
<style lang="stylus" scoped>
.company-remark {
  margin 0 0 2px;
}
</style>
